import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Sparkles } from 'lucide-react';
import { usePokemonSearchIndex } from '../hooks/usePokemonSearchIndex';
import { highlightMatch, normalizeQuery } from '../utils/smartSearchEngine';
import { getPokemonImage } from '../utils/pokemonImageResolver';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * SMART SEARCH BAR
 * Modern, mobile-first search experience
 */
const SmartSearchBar = ({ 
  placeholder: placeholderProp,
  onSearch,
  className = "",
  autoFocus = false
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const placeholder = placeholderProp || t('home.search.placeholder');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  const { search, loading: indexLoading } = usePokemonSearchIndex();
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  
  // Calculate dropdown position based on input boundingRect
  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8, // 8px = mt-2 equivalent
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, []);
  
  // Update position when results are shown or window resizes/scrolls
  useEffect(() => {
    if (showResults) {
      updateDropdownPosition();
      
      const handleResize = () => updateDropdownPosition();
      const handleScroll = () => updateDropdownPosition();
      
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true); // Capture scroll events from all elements
      
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [showResults, updateDropdownPosition]);
  
  // Debounced search
  const performSearch = useCallback((searchQuery) => {
    if (!searchQuery || searchQuery.trim().length === 0) {
      setResults([]);
      setIsSearching(false);
      return;
    }
    
    setIsSearching(true);
    
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Debounce search (350ms for instant feel)
    // Pokédex-grade: strict relevance threshold applied in search engine
    debounceTimerRef.current = setTimeout(() => {
      const searchResults = search(searchQuery, { 
        limit: 8, // Show top 8 most relevant results (quality over quantity)
        minScore: 200 // Minimum relevance threshold (strict filtering)
      });
      setResults(searchResults);
      setIsSearching(false);
    }, 350);
  }, [search]);
  
  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    if (value.trim().length > 0) {
      setShowResults(true);
      performSearch(value);
    } else {
      setResults([]);
      setShowResults(false);
    }
  };
  
  // Handle result click
  const handleResultClick = (pokemon) => {
    const pokemonId = pokemon.id || pokemon.Pokemon_ID;
    
    // Save to recent searches
    const recentSearches = JSON.parse(localStorage.getItem('smartSearch_recent') || '[]');
    const newRecent = [
      {
        id: pokemonId,
        name_en: pokemon.Pokemon_Name_EN || pokemon.name_en || pokemon.name_english,
        name_th: pokemon.Pokemon_Name_TH || pokemon.name_th || pokemon.name_thai,
        query: query
      },
      ...recentSearches.filter(r => r.id !== pokemonId)
    ].slice(0, 5);
    localStorage.setItem('smartSearch_recent', JSON.stringify(newRecent));
    
    // Navigate or call callback
    if (onSearch) {
      onSearch(pokemon);
    } else {
      navigate(`/pokemon/${pokemonId}`);
    }
    
    // Reset
    setQuery('');
    setResults([]);
    setShowResults(false);
    
    // Blur input on mobile
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };
  
  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (results.length > 0) {
      handleResultClick(results[0]);
    } else if (query.trim()) {
      // Navigate to search page with query
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setQuery('');
      setResults([]);
      setShowResults(false);
    }
  };
  
  // Clear search
  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  
  // Close results on outside click (updated for Portal)
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both searchRef and the portal dropdown
      const dropdownElement = document.querySelector('[data-smart-search-dropdown]');
      if (
        searchRef.current && 
        !searchRef.current.contains(event.target) &&
        dropdownElement &&
        !dropdownElement.contains(event.target)
      ) {
        setShowResults(false);
      }
    };
    
    // Use capture phase to catch events before they bubble
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('touchend', handleClickOutside, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchend', handleClickOutside, true);
    };
  }, [showResults]);
  
  // Load recent searches
  const [recentSearches, setRecentSearches] = useState([]);
  useEffect(() => {
    const recent = JSON.parse(localStorage.getItem('smartSearch_recent') || '[]');
    setRecentSearches(recent);
  }, []);
  
  // Pokemon image is now handled via imported getPokemonImage utility
  // No local function needed - use the shared utility directly
  
  const normalizedQuery = normalizeQuery(query);
  
  return (
    <div ref={searchRef} className={`relative z-50 ${className}`}>
      <form onSubmit={handleSubmit} className="relative">
        {/* Search Input - Large, Mobile-First */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6 pointer-events-none" />
          
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => {
              if (query.trim() || results.length > 0 || recentSearches.length > 0) {
                setShowResults(true);
              }
            }}
            placeholder={placeholder}
            autoComplete="off"
            autoFocus={autoFocus}
            className={`
              w-full
              pl-14 pr-12
              py-4
              text-lg
              bg-white
              border-2 border-gray-300
              rounded-2xl
              focus:outline-none
              focus:border-blue-500
              focus:ring-4 focus:ring-blue-100
              transition-all duration-200
              placeholder:text-gray-400
              ${indexLoading ? 'opacity-70' : ''}
            `}
            style={{
              fontSize: 'clamp(16px, 4vw, 18px)', // Responsive font size
              minHeight: '56px' // Touch-friendly height
            }}
          />
          
          {/* Loading Indicator */}
          {isSearching && (
            <div className="absolute right-12 top-1/2 transform -translate-y-1/2">
              <Sparkles className="w-5 h-5 text-blue-500 animate-pulse" />
            </div>
          )}
          
          {/* Clear Button */}
          {query && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Clear search"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </form>
      
      {/* Results Dropdown - Rendered via Portal */}
      {typeof window !== 'undefined' && createPortal(
        <AnimatePresence>
          {showResults && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="fixed bg-white rounded-2xl shadow-2xl border border-gray-200 max-h-[60vh] overflow-y-auto"
              data-smart-search-dropdown
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                zIndex: 9999,
                WebkitOverflowScrolling: 'touch' // Smooth scrolling on iOS
              }}
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside dropdown
            >
            {/* Recent Searches (when no query) */}
            {!query && recentSearches.length > 0 && (
              <div className="p-4 border-b border-gray-100">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  ค้นหาล่าสุด
                </div>
                <div className="space-y-1">
                  {recentSearches.map((recent, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        navigate(`/pokemon/${recent.id}`);
                        setShowResults(false);
                      }}
                      className="w-full text-left px-4 py-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                      style={{ minHeight: '48px' }} // Touch-friendly
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 text-base">
                            {recent.name_th || recent.name_en}
                          </div>
                          {recent.name_en && recent.name_th && (
                            <div className="text-sm text-gray-500 mt-0.5">
                              {recent.name_en}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Search Results */}
            {query && (
              <div className="py-2">
                {results.length > 0 ? (
                  <div className="space-y-1">
                    {results.map((pokemon, index) => {
                      const pokemonId = pokemon.id || pokemon.Pokemon_ID;
                      const nameEn = pokemon.Pokemon_Name_EN || pokemon.name_en || pokemon.name_english || '';
                      const nameTh = pokemon.Pokemon_Name_TH || pokemon.name_th || pokemon.name_thai || '';
                      const imageUrl = getPokemonImage(pokemon); // Uses shared utility function
                      
                      const nameEnParts = highlightMatch(nameEn, query, normalizedQuery);
                      const nameThParts = highlightMatch(nameTh, query, normalizedQuery);
                      
                      return (
                        <button
                          key={pokemonId || index}
                          onClick={() => handleResultClick(pokemon)}
                          className="w-full text-left px-4 py-4 rounded-xl hover:bg-blue-50 active:bg-blue-100 transition-colors touch-manipulation border-b border-gray-50 last:border-b-0"
                          style={{ minHeight: '64px' }} // Touch-friendly with image
                        >
                          <div className="flex items-center space-x-4">
                            {/* Pokemon Image */}
                            <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center border-2 border-gray-300 overflow-hidden">
                              <img
                                src={imageUrl}
                                alt={nameTh || nameEn}
                                className="w-full h-full object-contain p-1"
                                onError={(e) => {
                                  e.target.src = '/images/pokemon-spirit/000.png';
                                }}
                              />
                            </div>
                            
                            {/* Pokemon Info */}
                            <div className="flex-1 min-w-0">
                              {/* Thai Name with Highlight */}
                              {nameTh && (
                                <div className="font-bold text-gray-900 text-lg mb-1">
                                  {nameThParts.map((part, i) => (
                                    <span
                                      key={i}
                                      className={part.matched ? 'bg-yellow-200 text-yellow-900 px-1 rounded' : ''}
                                    >
                                      {part.text}
                                    </span>
                                  ))}
                                </div>
                              )}
                              
                              {/* English Name with Highlight */}
                              {nameEn && (
                                <div className="text-sm text-gray-600 mb-1">
                                  {nameEnParts.map((part, i) => (
                                    <span
                                      key={i}
                                      className={part.matched ? 'bg-yellow-200 text-yellow-900 px-1 rounded' : ''}
                                    >
                                      {part.text}
                                    </span>
                                  ))}
                                </div>
                              )}
                              
                              {/* ID Badge */}
                              <div className="inline-flex items-center mt-1">
                                <span className="text-xs font-mono font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                  #{String(pokemonId).padStart(3, '0')}
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : isSearching ? (
                  <div className="p-8 text-center">
                    <Sparkles className="w-8 h-8 text-blue-500 animate-pulse mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">{t('common.loading')}</p>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium mb-1">{t('common.noData')}</p>
                    <p className="text-gray-400 text-sm">
                      ลองค้นหาด้วยชื่อไทย, ชื่ออังกฤษ หรือเลขไอดี
                    </p>
                  </div>
                )}
              </div>
            )}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default SmartSearchBar;
